import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Mic, RotateCcw } from 'lucide-react';
import { cn } from 'lib/utils';
import { useIsMobile } from 'hooks/use-media-query';
import * as Api from '../../api';
import { canFollowUp, formatClock, isSpokenAnswer, speakingPrompts, withFollowUps, type SpeakingPrompt } from '../../constants';
import { useSpeakingRecorder } from '../../hooks/useSpeakingRecorder';
import { askAloud, unlockAudio } from './examinerVoice';
import { VoiceOrb } from './VoiceOrb';
import type { OrbState } from './voiceOrbMotion';
import type { CandidateSection, SpeakingAnswer, SpeakingRecording } from '../../types';

/** The answer starts by itself this long after the question, as an examiner would wait. */
const AUTO_START_SECONDS = 5;
const UPLOAD_RETRY_DELAYS_MS = [1000, 3000];
/** Longer than the server ever takes (about 32s worst case), so a question is never stored after we stopped waiting for it. */
const FOLLOW_UP_TIMEOUT_MS = 40_000;
const FOLLOW_UP_KEY_SUFFIX = /-f1$/;
const RING_RADIUS = 42;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;
const ORB_SIZE = 260;
const ORB_SIZE_MOBILE = 120;

type Phase = 'intro' | 'asking' | 'prep' | 'ready' | 'recording' | 'saving' | 'failed' | 'done';

interface Props {
  attemptId: string;
  section: CandidateSection;
  answers: Map<string, unknown>;
  /** Mirrors a stored recording into the session so progress and counts stay right. */
  onRecorded: (questionId: string, value: SpeakingAnswer) => void;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * IELTS Speaking: the examiner's question is read aloud (a recorded examiner
 * voice, else the browser's), the candidate answers
 * into the microphone and each answer uploads as soon as it ends. Nothing can
 * be re-recorded, and a reload resumes at the first unanswered question.
 */
export function SpeakingSectionView({ attemptId, section, answers, onRecorded }: Props) {
  const { t } = useTranslation();
  const question = section.questions.find((q) => q.type === 'SPEAKING');
  const scriptedPrompts = useMemo(() => speakingPrompts(question?.speakingParts), [question?.speakingParts]);
  const stored = (question ? answers.get(question.id) : undefined) as SpeakingAnswer | undefined;
  const recorded = useMemo(() => stored ?? {}, [stored]);
  // The live examiner's follow-ups join the scripted list as they are asked, so a reload resumes at one.
  const prompts = useMemo(() => withFollowUps(scriptedPrompts, recorded), [scriptedPrompts, recorded]);
  const index = prompts.findIndex((p) => !isSpokenAnswer(recorded[p.key]));
  const current: SpeakingPrompt | undefined = index >= 0 ? prompts[index] : undefined;

  const currentKeyRef = useRef(current?.key);
  currentKeyRef.current = current?.key;
  // The clock can close the section mid-answer; the API allows a short grace, so still send it.
  const recorder = useSpeakingRecorder((audio) => {
    const key = currentKeyRef.current;
    if (!question || !key) return;
    Api.UploadSpeaking(attemptId, section.id, question.id, key, audio.blob, audio.durationSec).catch(() => undefined);
  });
  const { requestMic, start: startRecording, stop: stopRecording } = recorder;
  const [phase, setPhase] = useState<Phase>(index < 0 ? 'done' : 'intro');
  const [repeated, setRepeated] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [notes, setNotes] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [examinerListening, setExaminerListening] = useState(false);
  const pendingRef = useRef<{ key: string; blob: Blob; durationSec: number } | null>(null);
  const cancelSpeechRef = useRef<() => void>(() => undefined);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const examinerUrls = useRef(new Map<string, Promise<string | null>>());

  const isMobile = useIsMobile();

  useEffect(() => () => cancelSpeechRef.current(), []);

  // One request per prompt; a failed one is forgotten so the next ask retries.
  const examinerUrl = useCallback(
    (key: string) => {
      const cached = examinerUrls.current.get(key);
      if (cached) return cached;
      // A follow-up arrives with its own recording, or is read in the browser's voice after a reload.
      if (!question || FOLLOW_UP_KEY_SUFFIX.test(key)) return Promise.resolve(null);
      const request = Api.ExaminerAudio(attemptId, section.id, question.id, key)
        .then(({ data }) => data.url ?? null)
        .catch(() => {
          examinerUrls.current.delete(key);
          return null;
        });
      examinerUrls.current.set(key, request);
      return request;
    },
    [attemptId, section.id, question],
  );

  // Fetch this question and the next ahead of time, so the examiner speaks without a pause.
  useEffect(() => {
    if (index >= 0) prompts.slice(index, index + 2).forEach((p) => examinerUrl(p.key));
  }, [index, prompts, examinerUrl]);

  const ask = useCallback((prompt: SpeakingPrompt) => {
    setPhase('asking');
    cancelSpeechRef.current();
    if (!audioRef.current) audioRef.current = new Audio();
    cancelSpeechRef.current = askAloud(audioRef.current, examinerUrl(prompt.key), prompt.text, () => {
      if (prompt.prepSeconds > 0) {
        setCountdown(prompt.prepSeconds);
        setPhase('prep');
      } else {
        setCountdown(AUTO_START_SECONDS);
        setPhase('ready');
      }
    });
  }, [examinerUrl]);

  const begin = async () => {
    if (!current) return;
    // Before the first await: the tap that starts the test is what lets the examiner be heard (see unlockAudio).
    if (!audioRef.current) audioRef.current = new Audio();
    unlockAudio(audioRef.current);
    if (await requestMic()) ask(current);
  };

  const startAnswer = useCallback(() => {
    cancelSpeechRef.current();
    if (startRecording()) setPhase('recording');
  }, [startRecording]);

  // Prep and auto-start countdowns tick once a second.
  useEffect(() => {
    if (phase !== 'prep' && phase !== 'ready') return undefined;
    if (countdown <= 0) {
      startAnswer();
      return undefined;
    }
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, countdown, startAnswer]);

  // The live examiner's follow-up to the answer just saved, or null: it is off, has nothing to ask, or is too slow.
  const askFollowUp = useCallback(
    async (promptKey: string): Promise<{ key: string; text: string } | null> => {
      const asked = prompts.find((p) => p.key === promptKey);
      if (!question || !asked || !canFollowUp(asked)) return null;
      setExaminerListening(true);
      try {
        const { data } = await Api.SpeakingFollowUp(attemptId, section.id, question.id, promptKey, FOLLOW_UP_TIMEOUT_MS);
        if (!data.key || !data.text) return null;
        examinerUrls.current.set(data.key, Promise.resolve(data.url ?? null));
        return { key: data.key, text: data.text };
      } catch {
        // Never costs the candidate the test: the scripted questions carry on.
        return null;
      } finally {
        setExaminerListening(false);
      }
    },
    [attemptId, section.id, question, prompts],
  );

  const upload = useCallback(async () => {
    const pending = pendingRef.current;
    if (!pending || !question) return;
    setPhase('saving');
    setUploadError('');
    // The stored answer, plus the follow-up the examiner asked about it (stored together so the next question is ready).
    const settle = async (entry: SpeakingRecording, canAskFollowUp: boolean) => {
      pendingRef.current = null;
      const followUp = canAskFollowUp ? await askFollowUp(pending.key) : null;
      onRecorded(question.id, {
        ...recorded,
        [pending.key]: { ...recorded[pending.key], ...entry },
        ...(followUp ? { [followUp.key]: { prompt: followUp.text, askedAt: new Date().toISOString() } } : {}),
      });
    };
    for (let attempt = 0; ; attempt += 1) {
      try {
        const { data } = await Api.UploadSpeaking(attemptId, section.id, question.id, pending.key, pending.blob, pending.durationSec);
        await settle(
          { audioKey: '', durationSec: data.durationSec ?? pending.durationSec, recordedAt: new Date().toISOString() },
          data.followUp === true,
        );
        break;
      } catch (e: any) {
        const code = e?.response?.data?.code;
        // Stored already, or a follow-up the server no longer has: either way there is nothing left to send.
        if (code === 'ALREADY_RECORDED' || code === 'FOLLOW_UP_NOT_ISSUED') {
          await settle({ audioKey: '' }, false);
          break;
        }
        if (attempt >= UPLOAD_RETRY_DELAYS_MS.length || code === 'SECTION_TIME_UP' || code === 'SECTION_LOCKED') {
          setUploadError(e?.response?.data?.message || t('exam.speaking.upload_failed'));
          setPhase('failed');
          return;
        }
        await wait(UPLOAD_RETRY_DELAYS_MS[attempt]);
      }
    }
  }, [attemptId, section.id, question, recorded, onRecorded, askFollowUp, t]);

  const finishAnswer = useCallback(async () => {
    if (!current) return;
    const audio = await stopRecording();
    if (!audio) return;
    pendingRef.current = { key: current.key, blob: audio.blob, durationSec: audio.durationSec };
    await upload();
  }, [current, stopRecording, upload]);

  // The answer stops itself when its time is up.
  useEffect(() => {
    if (phase === 'recording' && current && recorder.elapsed >= current.answerSeconds) finishAnswer();
  }, [phase, current, recorder.elapsed, finishAnswer]);

  // Once an answer is stored, the next question is asked straight away.
  const lastKeyRef = useRef(current?.key);
  useEffect(() => {
    if (current?.key === lastKeyRef.current) return;
    lastKeyRef.current = current?.key;
    setRepeated(false);
    if (!current) {
      setPhase('done');
      return;
    }
    if (current.cueCard) setNotes('');
    if (phase !== 'intro') ask(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.key]);

  const repeat = () => {
    if (!current || repeated) return;
    setRepeated(true);
    ask(current);
  };

  if (!question || !prompts.length) {
    return <p className="p-8 text-center text-gray-600">{t('exam.empty_section')}</p>;
  }

  const orbState: OrbState =
    phase === 'asking' ? 'examiner' : phase === 'recording' ? 'candidate' : phase === 'saving' && examinerListening ? 'thinking' : 'idle';
  // Numbered by the scripted questions: a follow-up is not one of them.
  const partPrompts = current ? scriptedPrompts.filter((p) => p.part === current.part) : [];
  const numberInPart = current ? partPrompts.findIndex((p) => p.key === current.key) + 1 : 0;
  const recordedCount = prompts.filter((p) => isSpokenAnswer(recorded[p.key])).length;
  const candidateTurn = phase === 'recording';
  const asking = phase !== 'intro' && phase !== 'done' ? current : undefined;

  return (
    <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-[#F8FAFC]">
      <StageBackdrop candidate={candidateTurn} />
      <div className="relative mx-auto flex min-h-full w-full min-w-0 max-w-5xl flex-col px-4 sm:px-8">
        <StageHeader
          prompts={scriptedPrompts}
          recorded={recorded}
          currentKey={asking?.key.replace(FOLLOW_UP_KEY_SUFFIX, '')}
          part={asking?.part}
          savedCount={recordedCount}
        />

        <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-3 py-2 text-center sm:gap-5 sm:py-4">
          {phase === 'intro' ? (
            <MicCheck status={recorder.micStatus} level={recorder.level} resuming={recordedCount > 0} onStart={begin} />
          ) : phase === 'done' ? (
            <div className="my-auto flex flex-col items-center gap-4 text-center">
              <span className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <Check className="h-9 w-9 text-green-700" aria-hidden />
              </span>
              <h2 className="font-display text-2xl font-bold text-[#0A1628]">{t('exam.speaking.all_done')}</h2>
              <p className="max-w-md text-sm text-gray-600">{t('exam.speaking.all_done_help')}</p>
            </div>
          ) : current ? (
            <>
              {/* Moves like speech while the examiner asks; follows the candidate's microphone while they speak. */}
              <VoiceOrb state={orbState} level={candidateTurn ? recorder.level : undefined} size={isMobile ? ORB_SIZE_MOBILE : ORB_SIZE} />

              <p
                className={cn(
                  'text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-500',
                  candidateTurn ? 'text-sky-700' : 'text-lime-800',
                )}
              >
                {current.followUp
                  ? t('exam.speaking.follow_up_label', { part: current.part })
                  : t('exam.speaking.examiner_label', { part: current.part, n: numberInPart, total: partPrompts.length })}
              </p>

              {current.cueCard ? (
                <CueCard text={current.text} notes={notes} onNotes={setNotes} />
              ) : (
                <p
                  className={cn(
                    'max-w-3xl break-words font-display font-bold leading-snug tracking-tight transition-colors duration-500 sm:leading-tight',
                    candidateTurn ? 'text-lg text-slate-500 sm:text-[30px]' : 'text-[22px] text-[#0A1628] sm:text-[44px]',
                  )}
                >
                  {current.text}
                </p>
              )}

              {candidateTurn ? (
                <div className="flex flex-col items-center gap-2 sm:gap-4">
                  <span className="font-mono text-4xl font-semibold leading-none tracking-tight text-[#0A1628] sm:text-[52px]">
                    {formatClock(Math.floor(recorder.elapsed))}{' '}
                    <span className="text-xl text-slate-400">/ {formatClock(current.answerSeconds)}</span>
                  </span>
                  <LevelBars level={recorder.level} large />
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-center gap-x-3.5 gap-y-2 text-sm text-slate-500">
                  <span>{t('exam.speaking.played_once')}</span>
                  <button
                    type="button"
                    onClick={repeat}
                    disabled={repeated || phase !== 'ready'}
                    title={t('exam.speaking.repeat_help')}
                    className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-[13px] font-semibold text-[#0A1628] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    {t('exam.speaking.repeat')}
                  </button>
                </div>
              )}
            </>
          ) : null}
        </div>

        {asking && (
          <AnswerDock
            phase={phase}
            countdown={countdown}
            prepSeconds={asking.prepSeconds}
            elapsed={recorder.elapsed}
            answerSeconds={asking.answerSeconds}
            error={uploadError}
            examinerListening={examinerListening}
            onStart={startAnswer}
            onFinish={finishAnswer}
            onRetry={upload}
          />
        )}
      </div>
    </div>
  );
}

/** A faint tint behind the stage: volt while the examiner speaks, sky blue on the candidate's turn. */
function StageBackdrop({ candidate }: { candidate: boolean }) {
  const tint = (rgb: string) => `radial-gradient(900px 520px at 50% 38%, rgba(${rgb}, 0.2), rgba(${rgb}, 0) 70%)`;
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className={cn('absolute inset-0 transition-opacity duration-700', candidate ? 'opacity-0' : 'opacity-100')}
        style={{ background: tint('209, 254, 23') }}
      />
      <div
        className={cn('absolute inset-0 transition-opacity duration-700', candidate ? 'opacity-100' : 'opacity-0')}
        style={{ background: tint('56, 189, 248') }}
      />
    </div>
  );
}

function StageHeader({
  prompts,
  recorded,
  currentKey,
  part,
  savedCount,
}: {
  prompts: SpeakingPrompt[];
  recorded: SpeakingAnswer;
  currentKey?: string;
  part?: number;
  savedCount: number;
}) {
  const { t } = useTranslation();
  const parts = [1, 2, 3]
    .map((n) => ({ n, items: prompts.filter((p) => p.part === n) }))
    .filter((p) => p.items.length);
  return (
    <header className="flex flex-wrap items-center justify-between gap-x-8 gap-y-2.5 py-3 sm:gap-y-4 sm:py-5">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#D1FE17]">
          <Mic className="h-[18px] w-[18px] text-[#0A1628]" aria-hidden />
        </span>
        <div className="flex min-w-0 flex-col leading-tight">
          <b className="truncate text-[15px] font-bold text-[#0A1628]">{t('exam.speaking.type')}</b>
          {part ? <span className="truncate text-[12.5px] text-slate-500">{t(`exam.speaking.part${part}_short`)}</span> : null}
        </div>
      </div>

      {/* On a phone the bars take the whole row and share it, so twelve questions never push the page wider. */}
      <ol aria-label={t('exam.speaking.progress')} className="order-3 flex w-full min-w-0 items-start gap-3 sm:order-none sm:w-auto sm:gap-6">
        {parts.map((p) => {
          const done = p.items.filter((item) => isSpokenAnswer(recorded[item.key])).length;
          return (
            <li
              key={p.n}
              aria-label={`${t(`exam.speaking.part${p.n}_short`)}: ${done}/${p.items.length}`}
              style={{ flexGrow: p.items.length }}
              className="flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-none"
            >
              <div className="flex gap-1">
                {p.items.map((item) => (
                  <span
                    key={item.key}
                    aria-current={item.key === currentKey ? 'step' : undefined}
                    className={cn(
                      'h-1.5 min-w-0 flex-1 rounded-full sm:w-7 sm:flex-none',
                      isSpokenAnswer(recorded[item.key])
                        ? 'bg-[#0A1628]'
                        : item.key === currentKey
                          ? 'bg-[#D1FE17] ring-1 ring-[#0A1628]/40 motion-safe:animate-pulse'
                          : 'bg-slate-200',
                    )}
                  />
                ))}
              </div>
              <span className={cn('truncate text-[11px] font-semibold uppercase tracking-[0.08em]', p.n === part ? 'text-[#0A1628]' : 'text-slate-500')}>
                {t('exam.speaking.part_n', { n: p.n })}
              </span>
            </li>
          );
        })}
      </ol>

      <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-slate-900/[0.05] px-3 py-1.5 text-[12.5px] font-semibold text-slate-700 sm:px-3.5 sm:py-2">
        <Check className="h-3.5 w-3.5 text-lime-700" aria-hidden />
        {t('exam.speaking.saved_count', { count: savedCount })}
      </span>
    </header>
  );
}

function MicCheck({
  status,
  level,
  resuming,
  onStart,
}: {
  status: ReturnType<typeof useSpeakingRecorder>['micStatus'];
  level: number;
  resuming: boolean;
  onStart: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const blocked = status === 'denied' || status === 'unsupported';
  const live = status === 'ready';
  return (
    <div className="my-auto flex max-w-lg flex-col items-center gap-5 text-center">
      {/* Answers the question "does my microphone work?" before the test starts. */}
      <VoiceOrb state={live ? 'candidate' : 'idle'} level={live ? level : undefined} size={isMobile ? 120 : 160} />
      <h2 className="font-display text-2xl font-bold text-[#0A1628]">
        {t(resuming ? 'exam.speaking.resume_title' : 'exam.speaking.intro_title')}
      </h2>
      <p className="text-sm leading-relaxed text-gray-600">{t('exam.speaking.intro_help')}</p>
      {live && <LevelBars level={level} />}
      {blocked && (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {t(status === 'denied' ? 'exam.speaking.mic_denied' : 'exam.speaking.mic_unsupported')}
        </p>
      )}
      <button
        type="button"
        onClick={onStart}
        disabled={status === 'asking' || status === 'unsupported'}
        className="h-12 rounded-full bg-[#0A1628] px-7 text-[15px] font-semibold text-white transition hover:brightness-110 active:scale-[0.98] disabled:opacity-50"
      >
        {t(resuming ? 'exam.speaking.resume' : 'exam.speaking.start')}
      </button>
    </div>
  );
}

function CueCard({ text, notes, onNotes }: { text: string; notes: string; onNotes: (v: string) => void }) {
  const { t } = useTranslation();
  const [topic, ...points] = text.split('\n').map((l) => l.trim()).filter(Boolean);
  return (
    <div className="grid w-full min-w-0 max-w-4xl grid-cols-1 gap-3 text-left sm:gap-4 md:grid-cols-2">
      <div className="min-w-0 break-words rounded-2xl border-2 border-[#0A1628] bg-[#FFFBEB] p-4 sm:p-5">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-amber-800">{t('exam.speaking.cue_card')}</p>
        <p className="mt-1.5 font-display text-lg font-bold leading-snug text-[#0A1628] sm:mt-2 sm:text-xl">{topic}</p>
        {points.length > 0 && (
          <ul className="mt-2 space-y-0.5 text-sm leading-snug text-gray-800 sm:mt-3 sm:space-y-1 sm:text-[15px] sm:leading-relaxed">
            {points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
      </div>
      <label className="flex flex-col gap-2 text-sm">
        <span className="font-medium text-gray-700">{t('exam.speaking.notes')}</span>
        <textarea
          value={notes}
          onChange={(e) => onNotes(e.target.value)}
          className="min-h-[76px] flex-1 resize-none rounded-2xl border border-gray-300 bg-white p-3 text-base leading-relaxed focus:outline-none sm:min-h-[150px] sm:p-4 sm:text-[15px]"
          placeholder={t('exam.speaking.notes_placeholder')}
        />
      </label>
    </div>
  );
}

interface DockProps {
  phase: Phase;
  countdown: number;
  prepSeconds: number;
  elapsed: number;
  answerSeconds: number;
  error: string;
  examinerListening: boolean;
  onStart: () => void;
  onFinish: () => void;
  onRetry: () => void;
}

/** The one control strip: what is happening, the record button with its time ring, and what a tap does now. */
function AnswerDock({ phase, countdown, prepSeconds, elapsed, answerSeconds, error, examinerListening, onStart, onFinish, onRetry }: DockProps) {
  const { t } = useTranslation();
  const progress =
    phase === 'recording'
      ? Math.min(1, elapsed / answerSeconds)
      : phase === 'prep'
        ? 1 - countdown / Math.max(1, prepSeconds)
        : phase === 'ready'
          ? 1 - countdown / AUTO_START_SECONDS
          : phase === 'saving'
            ? 1
            : 0;
  const red = phase === 'recording' || phase === 'failed';
  const stroke = red ? '#EF4444' : phase === 'saving' ? '#16A34A' : '#0A1628';

  const face =
    phase === 'recording'
      ? { label: t('exam.speaking.tap_to_finish'), aria: t('exam.speaking.finish_aria'), onClick: onFinish }
      : phase === 'failed'
        ? { label: t('exam.speaking.retry'), aria: t('exam.speaking.retry'), onClick: onRetry }
        : phase === 'ready' || phase === 'prep'
          ? {
              label: t(phase === 'prep' ? 'exam.speaking.start_talking' : 'exam.speaking.tap_to_answer'),
              aria: t('exam.speaking.start_aria'),
              onClick: onStart,
            }
          : null;
  const hint = face?.label ?? (phase === 'asking' ? t('exam.speaking.dock_asking_hint') : '');

  return (
    <footer className="sticky bottom-0 flex justify-center pb-[max(12px,env(safe-area-inset-bottom))] pt-2 sm:pb-5 sm:pt-3">
      <div className="flex w-full min-w-0 items-center justify-between gap-3 rounded-[32px] border border-slate-200 bg-white/90 py-2 pl-5 pr-3 shadow-[0_12px_36px_rgba(10,22,40,0.1)] backdrop-blur sm:w-auto sm:flex-wrap sm:justify-center sm:gap-x-6 sm:gap-y-3 sm:rounded-[40px] sm:py-3 sm:pl-8 sm:pr-7">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:min-w-[200px] sm:flex-none" aria-live="polite">
          <StatusLine phase={phase} countdown={countdown} error={error} examinerListening={examinerListening} />
          {/* A phone has no room for the hint beside the button, so it sits under the status. */}
          <span className="truncate text-xs text-slate-500 sm:hidden">{hint}</span>
        </div>

        <div className="relative h-[76px] w-[76px] shrink-0 sm:h-[92px] sm:w-[92px]">
          <svg viewBox="0 0 92 92" className="absolute inset-0 h-full w-full" aria-hidden>
            <circle cx="46" cy="46" r={RING_RADIUS} fill="none" stroke={red ? '#FEE2E2' : '#E2E8F0'} strokeWidth="4" />
            <circle
              cx="46"
              cy="46"
              r={RING_RADIUS}
              fill="none"
              stroke={stroke}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={RING_LENGTH}
              strokeDashoffset={RING_LENGTH * (1 - progress)}
              transform="rotate(-90 46 46)"
              className="transition-[stroke-dashoffset] duration-200 ease-linear"
            />
          </svg>
          <button
            type="button"
            onClick={face?.onClick}
            disabled={!face}
            aria-label={face?.aria ?? t('exam.speaking.listen')}
            className={cn(
              'absolute inset-[10px] flex items-center justify-center rounded-full transition sm:inset-3',
              'enabled:active:scale-[0.97] enabled:hover:brightness-110',
              red
                ? 'bg-[#EF4444] shadow-[0_10px_24px_rgba(239,68,68,0.35)]'
                : phase === 'saving'
                  ? 'bg-green-100'
                  : face
                    ? 'bg-[#0A1628] shadow-[0_10px_24px_rgba(10,22,40,0.25)]'
                    : 'bg-slate-100',
            )}
          >
            {phase === 'recording' ? (
              <span className="h-6 w-6 rounded-md bg-white" aria-hidden />
            ) : phase === 'saving' ? (
              <Check className="h-8 w-8 text-green-700" aria-hidden />
            ) : phase === 'failed' ? (
              <RotateCcw className="h-7 w-7 text-white" aria-hidden />
            ) : phase === 'prep' ? (
              <span className="font-mono text-xl font-semibold text-white" aria-hidden>
                {formatClock(countdown)}
              </span>
            ) : (
              <Mic className={cn('h-7 w-7', face ? 'text-primary' : 'text-slate-400')} aria-hidden />
            )}
          </button>
        </div>

        <span className="hidden min-w-[150px] text-left text-sm text-slate-500 sm:block">{hint}</span>
      </div>
    </footer>
  );
}

function StatusLine({
  phase,
  countdown,
  error,
  examinerListening,
}: {
  phase: Phase;
  countdown: number;
  error: string;
  examinerListening: boolean;
}) {
  const { t } = useTranslation();
  if (phase === 'failed') return <span role="alert" className="text-sm font-medium text-red-700">{error}</span>;
  const recording = phase === 'recording';
  const text = recording
    ? t('exam.speaking.recording')
    : phase === 'asking'
      ? t('exam.speaking.examiner_speaking')
      : phase === 'prep'
        ? t('exam.speaking.prep_status', { seconds: countdown })
        : phase === 'ready'
          ? t('exam.speaking.auto_start', { seconds: countdown })
          : phase === 'saving'
            ? t(examinerListening ? 'exam.speaking.examiner_thinking' : 'exam.speaking.saving_status')
            : '';
  if (!text) return null;
  return (
    <span className={cn('inline-flex items-center gap-2.5 text-sm font-semibold', recording ? 'text-red-700' : 'text-slate-600')}>
      <span
        className={cn(
          'h-2.5 w-2.5 shrink-0 rounded-full motion-safe:animate-pulse',
          recording ? 'bg-red-500' : phase === 'saving' ? 'bg-green-600' : 'bg-[#D1FE17] shadow-[0_0_0_2px_#0A1628]',
        )}
        aria-hidden
      />
      {text}
    </span>
  );
}

/** One symmetric run of bars, so the meter reads as a waveform. */
const BAR_SHAPE = [0.3, 0.55, 0.85, 0.6, 1, 0.4, 0.8, 0.65, 0.95, 0.35, 0.75, 0.5, 0.25, 0.45];
const BAR_SHAPE_LARGE = [...BAR_SHAPE, ...[...BAR_SHAPE].reverse()];

function LevelBars({ level, large = false }: { level: number; large?: boolean }) {
  const { t } = useTranslation();
  const pct = Math.round(level * 100);
  const shape = large ? BAR_SHAPE_LARGE : BAR_SHAPE;
  const max = large ? 52 : 32;
  return (
    <div
      className={cn('flex items-center', large ? 'h-[52px] gap-[3px]' : 'h-8 gap-1')}
      role="meter"
      aria-label={t('exam.speaking.voice_level')}
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {shape.map((s, i) => {
        const h = Math.max(6, Math.round(max * Math.min(1, level * 1.4) * s));
        return (
          <span
            key={i}
            className={cn('rounded-full transition-[height] duration-75', large ? 'w-1' : 'w-1.5', h > 6 ? 'bg-sky-500' : 'bg-slate-300')}
            style={{ height: h }}
          />
        );
      })}
    </div>
  );
}

export default SpeakingSectionView;
